import React from 'react';
import { CheckCircle, Lock, DollarSign, User, FileText, Camera, Sparkles, Radio, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

/**
 * SetupChecklistCard Component
 * 
 * Shows a setup progress checklist for reviewers who haven't completed their setup.
 * 
 * 5 Requirements for Visibility (in order):
 * 1. Choose Username (auto-filled at signup - easy win)
 * 2. Add Profile Photo
 * 3. Write Bio (80+ chars)
 * 4. Connect Stripe to Get Paid
 * 5. Start Your First Session
 */
const SetupChecklistCard = ({ 
  user, 
  onConnectStripe,
  onGoLive,
  onEditProfile, 
  onDismiss,
  isGrandfathered = false,
  variant = 'prominent' // 'prominent' or 'gentle'
}) => {
  if (!user) return null;

  // Calculate what's complete
  const hasUsername = !!user.username;
  const hasAvatar = !!(user.avatar || user.profile_image || user.avatarUrl);
  const hasBio = !!(user.bio && user.bio.length >= 80);
  
  // Check monetization plan
  const isProPlan = user.monetization_plan === 'subscription_cashapp_venmo';
  const isSubscriptionActive = user.subscription_status === 'active' || user.subscription_status === 'trialing';
  const hasCashAppOrVenmo = !!(user.cash_app || user.venmo);
  
  // Stripe is only complete when details_submitted is TRUE
  const hasStripe = user.stripe_details_submitted === true;
  
  // Payment method is complete if:
  // - Free Plan: Has Stripe
  // - Pro Plan: Has Stripe OR has Cash App/Venmo (with active subscription)
  const hasPaymentMethod = hasStripe || (isProPlan && isSubscriptionActive && hasCashAppOrVenmo);
  
  // Check if user has started their first session
  const totalSessions = user.total_sessions || user.totalSessions || 0;
  // First session is only "complete" if payment method is set up
  const hasStartedFirstSession = hasPaymentMethod && totalSessions > 0;

  // Determine Step 4 label based on plan
  const getPaymentStepLabel = () => {
    if (isProPlan && isSubscriptionActive) {
      if (hasStripe) return 'Payment method connected';
      return 'Add payment method (Stripe, Cash App, or Venmo)';
    }
    return 'Connect Stripe to get paid';
  };

  // 5 requirements in correct order
  const steps = [
    { id: 'username', label: 'Choose username', complete: hasUsername, icon: User },
    { id: 'avatar', label: 'Add profile photo', complete: hasAvatar, icon: Camera },
    { id: 'bio', label: 'Write bio (80+ chars)', complete: hasBio, icon: FileText },
    { id: 'payment', label: getPaymentStepLabel(), complete: hasPaymentMethod, icon: DollarSign, highlight: !hasPaymentMethod },
    { id: 'firstsession', label: 'Start your first session', complete: hasStartedFirstSession, icon: Radio, highlight: hasPaymentMethod && !hasStartedFirstSession, requiresPayment: true, locked: !hasPaymentMethod }
  ];

  const completedCount = steps.filter(s => s.complete).length;
  const progressPercent = (completedCount / steps.length) * 100;
  const allComplete = completedCount === steps.length;

  // If everything is complete, don't show anything
  if (allComplete) return null;

  // For grandfathered users who only need Stripe, show gentle nudge
  if (isGrandfathered && variant === 'gentle') {
    // Only show if Stripe is not connected
    if (hasStripe) return null;

    return (
      <div 
        className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 mb-6"
        data-testid="setup-nudge-card"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-purple-900 mb-1">Start Earning Money</h3>
              <p className="text-sm text-purple-700">
                Connect Stripe to receive payments directly to your bank. Takes 2 minutes!
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={onConnectStripe}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              size="sm"
            >
              Connect Stripe
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            {onDismiss && (
              <button 
                onClick={onDismiss}
                className="text-purple-400 hover:text-purple-600 text-sm"
              >
                Later
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Prominent checklist for non-visible users
  return (
    <div 
      className="bg-white border-2 border-amber-300 rounded-2xl p-6 mb-6 shadow-lg"
      data-testid="setup-checklist-card"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Complete Your Setup</h2>
          <p className="text-sm text-gray-500">Finish these steps to start earning</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className={`text-sm font-semibold ${
            progressPercent === 100 
              ? 'text-green-600' 
              : progressPercent >= 80 
                ? 'text-yellow-600' 
                : progressPercent >= 60 
                  ? 'text-orange-600' 
                  : 'text-red-600'
          }`}>{completedCount} of {steps.length} complete</span>
        </div>
        <Progress 
          value={progressPercent} 
          className="h-3 bg-gray-100" 
          indicatorClassName={
            progressPercent === 100 
              ? 'bg-green-500' 
              : progressPercent >= 80 
                ? 'bg-yellow-400' 
                : progressPercent >= 60 
                  ? 'bg-orange-500' 
                  : 'bg-red-500'
          }
        />
      </div>

      {/* Steps */}
      <div className="space-y-3 mb-6">
        {steps.map((step, index) => {
          const isLocked = step.locked === true;
          const stepNumber = index + 1;
          
          return (
            <div 
              key={step.id}
              className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                step.complete 
                  ? 'bg-green-50 border border-green-200' 
                  : isLocked
                    ? 'bg-gray-100 border border-gray-200 opacity-60'
                  : step.highlight 
                    ? 'bg-purple-50 border-2 border-purple-300' 
                    : 'bg-gray-50 border border-gray-200'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step.complete 
                  ? 'bg-green-500' 
                  : isLocked
                    ? 'bg-gray-300'
                  : step.highlight 
                    ? 'bg-purple-200' 
                    : 'bg-gray-200'
              }`}>
                {step.complete ? (
                  <CheckCircle className="w-5 h-5 text-white" />
                ) : isLocked ? (
                  <Lock className="w-4 h-4 text-gray-500" />
                ) : (
                  <span className={`text-sm font-bold ${step.highlight ? 'text-purple-600' : 'text-gray-500'}`}>
                    {stepNumber}
                  </span>
                )}
              </div>
              <span className={`flex-1 ${
                step.complete 
                  ? 'text-green-700 line-through' 
                  : isLocked
                    ? 'text-gray-400'
                  : step.highlight 
                    ? 'text-purple-800 font-semibold' 
                    : 'text-gray-700'
              }`}>
                {step.label}
                {isLocked && <span className="text-xs ml-2">(Set up payment first)</span>}
              </span>
              {/* Payment method connect button */}
              {!step.complete && step.id === 'payment' && (
                <Button
                  onClick={onConnectStripe}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {isProPlan && isSubscriptionActive ? 'Set Up' : 'Connect'}
                </Button>
              )}
              {/* Start First Session button - only show if payment method connected and not locked */}
              {!step.complete && step.id === 'firstsession' && !isLocked && onGoLive && (
                <Button
                  onClick={onGoLive}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  Start Session
                </Button>
              )}
              {/* Edit profile button for other incomplete items */}
              {!step.complete && !isLocked && !['payment', 'firstsession'].includes(step.id) && (
                <Button
                  onClick={onEditProfile}
                  size="sm"
                  variant="outline"
                >
                  Edit
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SetupChecklistCard;
